import { useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useClubMembership } from '../hooks/useClubMembership'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRegisterBook } from '../hooks/useRegisterBook'
import type { RegisterEntry } from '../hooks/useRegisterBook'
import { formatDifferential, formatIndex } from '../lib/golfFormat'
import { findTier, foundingPlate } from '../lib/membership'
import type { FoundingPlate } from '../lib/membership'
import { romanYear } from '../lib/roman'
import {
  BookGate,
  ClubLinkButton,
  ClubPage,
  Container,
  Crest,
  EmptyPlate,
  Eyebrow,
  PageHead,
  Scorecard,
  SegmentedControl,
  SignInPrompt,
} from '../components/golf'
import type { ScorecardColumn, ScorecardRow, SegmentedOption } from '../components/golf'

/**
 * The Register (route map §4 `/clubhouse/register`) — standings, handicaps,
 * and the founding roll.
 *
 * The book is REAL: `GET /golf/register` is the ranked ledger of members in
 * good standing, index ascending with Unrated members last, and this page
 * renders exactly what it returns. It used to render `STANDING_ROWS` — four
 * hardcoded dashes — and an unconditional "No standings yet." plate beside
 * them; that was the biggest fiction left on the club.
 *
 * The columns and the expander are the MAINTAINER's call, recorded in
 * `docs/fireside/club-web-decisions.md` §6: the season book (No. · Member ·
 * Index · Rds · Best) ships by default, and his note on it, verbatim —
 *
 *   > Add an expand optoin to see the full book. i.e. people on desktop can
 *   > still see all easily.
 *
 * — is why "Show the full book" exists rather than a wider default table. Quote
 * kept exactly as written, typo included. Read that section before adding,
 * removing or reordering a column here; the Tier column is the one addition
 * beyond the option he was shown.
 *
 * Three things worth knowing before editing:
 *
 *   * **The rank is the BOOK's, not the column's.** It is numbered once, from
 *     the server's ordering, and travels with the member through all three
 *     views. Renumbering per sort would tell a member their rank changed
 *     because somebody pressed a button.
 *   * **Only a RATED member has a rank.** An Unrated member is not last, they
 *     are unplaced — fewer than three counting rounds is not a bad handicap.
 *     They read "—", and the club does not guess.
 *   * **"Best round" is gone from the standing card, not dashed.** The register
 *     publishes a best DIFFERENTIAL (which is in the ledger) and no best gross;
 *     a row that could only ever print a dash is worse than no row.
 */

/** Which of the three views over the one payload is showing. */
type View = 'leaderboard' | 'handicap' | 'founding'

const VIEWS: SegmentedOption<View>[] = [
  { value: 'leaderboard', label: 'Leaderboard' },
  { value: 'handicap', label: 'By handicap' },
  { value: 'founding', label: 'Founding' },
]

const CAPTIONS: Record<View, string> = {
  leaderboard: 'Members in good standing, ranked by handicap index',
  handicap: 'Members in good standing, by their low index',
  founding: 'Founding members, in the order their numbers were struck',
}

/** The season book. `Low` and `Trend` are revealed by the expander. */
const SEASON_COLUMNS: ScorecardColumn[] = [
  { key: 'rank', label: 'No.', numeric: true },
  { key: 'member', label: 'Member' },
  { key: 'tier', label: 'Tier' },
  { key: 'index', label: 'Index', numeric: true },
  { key: 'rounds', label: 'Rds', numeric: true },
  { key: 'best', label: 'Best', numeric: true },
]

const FULL_COLUMNS: ScorecardColumn[] = [
  ...SEASON_COLUMNS.slice(0, 4),
  { key: 'low', label: 'Low', numeric: true },
  ...SEASON_COLUMNS.slice(4),
  { key: 'trend', label: 'Trend', numeric: true },
]

/** A register line with the rank the BOOK gives it — null for the unplaced. */
interface BookLine extends RegisterEntry {
  rank: number | null
}

/**
 * Numbers the book once, in the server's own order. Unrated members are
 * skipped rather than numbered after the rated ones: they are unplaced, and a
 * rank printed beside "Unrated" would read as a position they have not earned.
 */
function numbered(register: RegisterEntry[]): BookLine[] {
  let rank = 0
  return register.map((entry) => ({
    ...entry,
    rank: entry.handicap_index === null ? null : ++rank,
  }))
}

/** Ascending, nulls last — the server's own ordering rule for the book. */
function ascendingNullsLast(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a - b
}

/**
 * One client-side view over the one payload. No view refetches: the register
 * is a single read, and three sorts of it are three sorts of it.
 */
function viewOf(lines: BookLine[], view: View): BookLine[] {
  if (view === 'handicap') {
    return [...lines].sort((a, b) => ascendingNullsLast(a.low_handicap_index, b.low_handicap_index))
  }
  if (view === 'founding') {
    // The order the numbers were STRUCK, which is the order a plate is
    // engraved in. Numbers are integers on this wire, not "No. 001" strings.
    return lines
      .filter((line) => line.tier === 'founding')
      .sort((a, b) => ascendingNullsLast(a.member_number, b.member_number))
  }
  return lines
}

/**
 * Which way a member's index has moved since their previous revision.
 *
 * Colour is NEVER the only channel: the glyph carries the direction for anyone
 * who cannot see the tint, and the visually-hidden word carries it for anyone
 * who cannot see the glyph. Up is the pin-flag red's text-safe sibling and down
 * is fairway — an index going UP is a handicap getting worse, which is the one
 * direction a member might want to notice.
 */
function trendCell(delta: number | null): React.ReactNode {
  if (delta === null) return '—'
  if (delta === 0) return <span className="text-ink-soft">Level</span>

  const up = delta > 0
  return (
    <span className={up ? 'text-flag-deep' : 'text-fairway'}>
      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
      <span className="sr-only">{up ? 'up ' : 'down '}</span>{' '}
      {Math.abs(delta).toFixed(1)}
    </span>
  )
}

/**
 * What the founding panel says about the plate, from the plate itself.
 *
 * `seats: 0` is the shell saying NOTHING (a screen rendered outside the engine
 * layout), and the honest answer there is the promise without a count — never
 * a hardcoded hundred, which is the drift `lib/membership` exists to remove.
 */
function foundingLine({ taken, seats }: FoundingPlate): string {
  if (seats === 0) return 'The charter closes when the last founding seat is claimed.'
  if (taken === 0) {
    return `All ${seats} founding seats are open, and none are yet taken. The charter closes when the last is claimed.`
  }
  if (taken >= seats) return `All ${seats} founding seats are struck. The charter is closed.`
  return `${taken} of ${seats} founding seats are spoken for. The charter closes when the last is claimed.`
}

/**
 * A standing as the club names it. The row's own `tier_name` is the server's
 * answer for EVERY tier the club defines, granted ones included; the shell's
 * tiers payload behind `findTier` knows only the purchasable three, so it is
 * the fallback for an older row, and the raw key is the last resort rather
 * than a silent blank.
 */
function tierName(line: Pick<BookLine, 'tier' | 'tier_name'>): string {
  return line.tier_name ?? findTier(line.tier)?.name ?? line.tier
}

function ledgerRows(lines: BookLine[], meId: string | undefined): ScorecardRow[] {
  return lines.map((line) => ({
    id: line.user_id,
    isYou: line.user_id === meId,
    cells: {
      rank: line.rank ?? '—',
      member: line.display_name,
      tier: tierName(line),
      index: formatIndex(line.handicap_index),
      low: line.low_handicap_index === null ? '—' : formatIndex(line.low_handicap_index),
      rounds: line.rounds_this_season,
      best: formatDifferential(line.best_differential),
      trend: trendCell(line.index_delta_recent),
    },
  }))
}

export function TheRegisterScreen() {
  usePageTitle('The Register')
  const { isAuthenticated, user } = useAuth()
  const { membership } = useClubMembership()
  const { register, isLoading, error, membershipRequired } = useRegisterBook(isAuthenticated)
  const [view, setView] = useState<View>('leaderboard')
  const [expanded, setExpanded] = useState(false)

  const name = user?.display_name?.trim() || user?.email?.split('@')[0]
  const plate = foundingPlate()
  const lines = useMemo(() => numbered(register), [register])
  const shown = useMemo(() => viewOf(lines, view), [lines, view])

  // The reader's own line, matched on the id the register carries — never on a
  // display name, which two members are eventually allowed to share.
  const me = lines.find((line) => line.user_id === user?.id)

  // Read from the standing, never asserted. No standing ⇒ no line at all,
  // which is the honest state for someone who has not taken one up.
  const since = membership?.standing_since
    ? `Est. ${romanYear(new Date(membership.standing_since).getUTCFullYear())}`
    : null
  const standing = membership
    ? [membership.member_number ?? 'No number struck', since].filter(Boolean).join(' · ')
    : null

  const settled = !isLoading && !membershipRequired && !error

  return (
    <ClubPage chrome="clubhouse">
      <PageHead
        eyebrow="The Register"
        title="Members in good"
        emphasis="standing."
        lede="Scores kept, handicaps honoured, discretion assured. Where the club stands, season by season."
      />

      <section className="pb-16">
        <Container>
          <p className="mb-5 text-[12.5px] tracking-[0.04em] text-ink-soft">
            {/* Counted from the register the server sent — never asserted, and
                absent entirely until there is a book to count. */}
            Season MMXXVI ·{' '}
            {register.length > 0
              ? `${register.length} in good standing`
              : 'the book opens with the first round played'}
          </p>

          <div className="grid items-start gap-6.5 lg:grid-cols-[1fr_328px]">
            {/* ── The ledger ──
                min-w-0: a grid item's min-width defaults to its content, so
                without it the ledger's table sets the COLUMN's width and the
                page scrolls sideways on a phone instead of the table scrolling
                inside its own overflow-x-auto. */}
            <div className="min-w-0 rounded-club-lg border border-ink/12 bg-parchment p-6">
              <BookGate
                isAuthenticated={isAuthenticated}
                membershipRequired={membershipRequired}
                error={error}
              >
                {isLoading && <p className="text-[13.5px] text-ink-soft">Opening the book…</p>}

                {!isLoading && register.length === 0 && (
                  <EmptyPlate
                    title="No standings yet."
                    description="The register ranks members once rounds are played. Nobody has teed off, so nobody is ahead of you."
                    action={
                      <ClubLinkButton to="/clubhouse/grounds" variant="ghost" size="sm">
                        See the grounds
                      </ClubLinkButton>
                    }
                  />
                )}

                {register.length > 0 && (
                  <>
                    {/* Wrap, don't hide: below 860px the control and the
                        expander stack rather than either disappearing. */}
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                      <SegmentedControl
                        label="How the register is ordered"
                        options={VIEWS}
                        value={view}
                        onChange={setView}
                      />
                      <button
                        type="button"
                        aria-expanded={expanded}
                        onClick={() => setExpanded((open) => !open)}
                        className="rounded-club-sm text-[13.5px] font-semibold text-fairway"
                      >
                        {expanded ? 'Hide the full book' : 'Show the full book'}
                      </button>
                    </div>

                    {shown.length === 0 ? (
                      <p className="py-4 text-[13.5px] text-ink-soft">
                        No founding seats are struck yet.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Scorecard
                          caption={CAPTIONS[view]}
                          columns={expanded ? FULL_COLUMNS : SEASON_COLUMNS}
                          rows={ledgerRows(shown, user?.id)}
                        />
                      </div>
                    )}
                  </>
                )}
              </BookGate>

              <p className="px-3.5 pb-1 pt-4 font-display text-[12.5px] italic text-ink-soft">
                The register settles nightly. No member&rsquo;s standing has ever suffered for
                leaving early.
              </p>
            </div>

            {/* ── Your standing ── */}
            <aside>
              <div className="rounded-club-lg border border-ink/12 bg-parchment p-6.5">
                <Eyebrow>Your standing</Eyebrow>

                {isAuthenticated ? (
                  <>
                    <p className="mt-2.5 font-display text-[27px] text-pine">{name}</p>
                    {/* Was "No number struck · Est. MMXXVI" — both halves were
                        asserted for every signed-in member regardless of the
                        truth. Now read from the standing, and omitted entirely
                        when there is none to read. */}
                    {standing && (
                      <p className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                        {standing}
                      </p>
                    )}

                    {me && (
                      <dl className="mt-4.5 border-t border-brass">
                        {[
                          { label: 'Rank', value: me.rank === null ? '—' : String(me.rank) },
                          { label: 'Handicap', value: formatIndex(me.handicap_index) },
                          {
                            label: 'Low index',
                            value:
                              me.low_handicap_index === null
                                ? '—'
                                : formatIndex(me.low_handicap_index),
                          },
                          {
                            label: 'Rounds, this season',
                            value: String(me.rounds_this_season),
                          },
                          { label: 'Standing', value: tierName(me) },
                        ].map((row) => (
                          <div
                            key={row.label}
                            className="flex items-baseline justify-between border-b border-brass/25 py-2.5"
                          >
                            <dt className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
                              {row.label}
                            </dt>
                            <dd className="font-display text-base tabular-nums text-pine">
                              {row.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}

                    {settled && !me && (
                      <p className="mt-5 text-center text-[11.5px] text-ink-soft">
                        Your line is written the first time you post a round.
                      </p>
                    )}
                  </>
                ) : (
                  <SignInPrompt what="your own line in the book" className="mt-4" />
                )}
              </div>

              {/* ── The founding hundred ── */}
              <div className="mt-5.5 rounded-club-lg border border-ink/12 bg-parchment p-6.5 text-center">
                <Crest monogram className="mx-auto mb-3 size-19" />
                <p className="font-display text-lg text-pine">The founding hundred</p>
                <p className="mb-4 mt-2 text-[12.5px] text-ink-soft">
                  {/* Read from the plate the shell injects, never asserted. The
                      sentence used to say "none are yet taken" whatever the
                      truth was. */}
                  {foundingLine(plate)}
                </p>
                <ClubLinkButton to="/the-club" variant="ghost" size="sm">
                  View the charter
                </ClubLinkButton>
              </div>
            </aside>
          </div>
        </Container>
      </section>
    </ClubPage>
  )
}
